import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  History,
  FolderOpen,
  Boxes,
  Printer,
  PackagePlus,
  Send,
  Building2,
  CheckCircle2,
  BarChart3,
  TrendingUp,
  ShoppingCart,
  Palette,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

// Interfaces
interface PrinterModel {
  id: string;
  modelNumber: string;
  modelName: string;
  printerType: 'LASER' | 'INKJET' | 'DOT_MATRIX' | 'THERMAL';
  isColor: boolean;
  compatibleTonerIds?: string[];
  compatibleToners?: ConsumableCategory[];
  createdAt: string;
}

interface ConsumableItem {
  id: string;
  name: string;
  description?: string;
  partNumber?: string;
  color?: string;
  category?: { id: string; name: string; isColor?: boolean };
  categoryId?: string;
  compatiblePrinters?: PrinterModel[];
  compatiblePrinterIds?: string[];
  compatibleAssets?: { id: string; assetTag: string; name: string }[];
  compatibleAssetIds?: string[];
  quantity: number;
  minimumStock: number;
  unit: string;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
  createdAt: string;
}

interface ConsumableCategory {
  id: string;
  name: string;
  description?: string;
  isColor?: boolean;
  colors?: string[];
  itemCount?: number;
  compatiblePrinterModelIds?: string[];
  compatiblePrinterModels?: PrinterModel[];
  createdAt: string;
}

interface Branch {
  id: string;
  name: string;
  code?: string;
}

interface StockLevel {
  id: string;
  branch?: { id: string; name: string };
  consumable?: { id: string; name: string; minimumStock?: number };
  item?: { id: string; name: string; partNumber?: string };
  quantity: number;
  unitPrice?: number;
  minimumStock?: number;
  status?: 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
}

interface ConsumableIssue {
  id: string;
  consumable: { id: string; name: string; color?: string };
  stock?: { id: string; branch?: { name: string } };
  issuedTo?: { id: string; firstName: string; lastName: string };
  issuedBy: { id: string; firstName: string; lastName: string };
  asset?: { id: string; assetTag: string; name: string; model?: string };
  quantity: number;
  reason?: string;
  purpose?: string;
  createdAt: string;
}

interface CategoryFormData {
  name: string;
  description: string;
  isColor?: boolean;
  colors?: string[];
  compatiblePrinterModelIds?: string[];
  compatibleAssetIds?: string[];
  // Per-color minimum stock levels
  minStockBlack?: number;
  minStockCyan?: number;
  minStockMagenta?: number;
  minStockYellow?: number;
}

interface ItemFormData {
  name: string;
  partNumber: string;
  description: string;
  categoryId: string;
  compatiblePrinterIds: string[];
  compatibleAssetIds: string[];
  minimumStock: number;
  unit: string;
  // Color variant support
  isColorCategory: boolean;
  colorVariants: Array<{ color: string; minimumStock: number }>;
}

interface StockFormData {
  consumableId: string;
  branchId: string;
  quantity: number;
  unitPrice: number;
  batchNumber: string;
  purchaseDate: string;
  invoiceNumber: string;
  supplierId: string;
}

interface IssueFormData {
  stockId: string;
  itemId: string;  // Required by API
  quantity: number;
  employeeId: string;
  printerId: string;  // Issue to printer asset
  branchId: string;   // Issue to branch
  departmentId: string; // Issue to department
  purpose: string;
}

interface PrinterModelFormData {
  modelNumber: string;
  modelName: string;
  printerType: 'LASER' | 'INKJET' | 'DOT_MATRIX' | 'THERMAL';
  isColor: boolean;
  colors: string[];
  compatibleTonerIds: string[];
}

const AVAILABLE_COLORS = [
  { value: 'black', label: 'Black', color: 'bg-gray-900' },
  { value: 'cyan', label: 'Cyan', color: 'bg-cyan-500' },
  { value: 'magenta', label: 'Magenta', color: 'bg-pink-500' },
  { value: 'yellow', label: 'Yellow', color: 'bg-yellow-400' },
];

const initialCategoryFormData: CategoryFormData = {
  name: '',
  description: '',
  compatiblePrinterModelIds: [],
  compatibleAssetIds: [],
  minStockBlack: 10,
  minStockCyan: 4,
  minStockMagenta: 4,
  minStockYellow: 4,
};

const initialItemFormData: ItemFormData = {
  name: '',
  partNumber: '',
  description: '',
  categoryId: '',
  compatiblePrinterIds: [],
  compatibleAssetIds: [],
  minimumStock: 0,
  unit: 'pcs',
  isColorCategory: false,
  colorVariants: [],
};

const initialStockFormData: StockFormData = {
  consumableId: '',
  branchId: '',
  quantity: 0,
  unitPrice: 0,
  batchNumber: '',
  purchaseDate: new Date().toISOString().split('T')[0],
  invoiceNumber: '',
  supplierId: '',
};

const initialIssueFormData: IssueFormData = {
  stockId: '',
  itemId: '',
  quantity: 1,
  employeeId: '',
  printerId: '',
  branchId: '',
  departmentId: '',
  purpose: '',
};

const initialPrinterModelFormData: PrinterModelFormData = {
  modelNumber: '',
  modelName: '',
  printerType: 'LASER',
  isColor: false,
  colors: [],
  compatibleTonerIds: [],
};

type TabType = 'items' | 'categories' | 'stock' | 'issues' | 'printers' | 'branch-report' | 'color-analytics' | 'printer-health';

export default function ConsumablesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<TabType>('items');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ConsumableCategory | null>(null);
  const [categoryFormData, setCategoryFormData] = useState<CategoryFormData>(initialCategoryFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'category' | 'item' | 'printer'>('category');

  // Item modal state
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ConsumableItem | null>(null);
  const [itemFormData, setItemFormData] = useState<ItemFormData>(initialItemFormData);

  // Stock modal state
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockFormData, setStockFormData] = useState<StockFormData>(initialStockFormData);

  // Issue modal state
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueFormData, setIssueFormData] = useState<IssueFormData>(initialIssueFormData);
  const [selectedPrinterForIssue, setSelectedPrinterForIssue] = useState<string>('');
  const [issueMode, setIssueMode] = useState<'standard' | 'quick'>('quick');

  // Printer Model modal state
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<PrinterModel | null>(null);
  const [printerFormData, setPrinterFormData] = useState<PrinterModelFormData>(initialPrinterModelFormData);

  // Fetch consumable items
  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ['consumables', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/consumables/items?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'items',
  });

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['consumable-categories', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/consumables/categories?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'categories',
  });

  // Fetch stock levels
  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ['consumable-stock', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/consumables/stock?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'stock',
  });

  // Fetch issue history
  const { data: issuesData, isLoading: issuesLoading } = useQuery({
    queryKey: ['consumable-issues', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/consumables/issues?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'issues',
  });

  // Fetch printer models
  const { data: printerModelsData, isLoading: printerModelsLoading } = useQuery({
    queryKey: ['printer-models', { page, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      const response = await api.get(`/consumables/printer-models?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'printers',
  });

  // Fetch all printer assets for compatibility selection and issue form
  const { data: printerAssetsData, isLoading: printerAssetsLoading } = useQuery({
    queryKey: ['printer-assets-all', showItemModal, showIssueModal, showCategoryModal, showPrinterModal],
    queryFn: async () => {
      // Fetch all assets in PRINTERS category
      const response = await api.get('/assets?category=PRINTERS&limit=1000');
      return response.data;
    },
    enabled: showItemModal || showIssueModal || showCategoryModal || showPrinterModal,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch all categories for item form and printer form
  const { data: allCategoriesData } = useQuery({
    queryKey: ['consumable-categories-all'],
    queryFn: async () => {
      const response = await api.get('/consumables/categories?limit=1000');
      return response.data;
    },
    enabled: showItemModal || showPrinterModal,
  });

  // Fetch branches for stock/issue forms
  const { data: branchesData } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await api.get('/branches?limit=1000');
      return response.data;
    },
    enabled: showStockModal || showIssueModal,
  });

  // Fetch departments for issue forms
  const { data: departmentsData } = useQuery({
    queryKey: ['departments-for-issue'],
    queryFn: async () => {
      const response = await api.get('/departments?limit=1000');
      return response.data;
    },
    enabled: showIssueModal,
  });

  // Fetch vendors/suppliers for stock form
  const { data: vendorsData } = useQuery({
    queryKey: ['vendors-for-stock'],
    queryFn: async () => {
      const response = await api.get('/vendors?limit=1000');
      return response.data;
    },
    enabled: showStockModal,
  });

  // Fetch all items for stock form
  const { data: allItemsData } = useQuery({
    queryKey: ['consumables-all'],
    queryFn: async () => {
      const response = await api.get('/consumables/items?limit=1000');
      return response.data;
    },
    enabled: showStockModal,
  });

  // Fetch all stock records for issue form
  const { data: allStockData } = useQuery({
    queryKey: ['consumable-stock-all'],
    queryFn: async () => {
      const response = await api.get('/consumables/stock?limit=1000');
      return response.data;
    },
    enabled: showIssueModal,
  });

  // Fetch branch printer toner report
  const { data: branchReportData, isLoading: branchReportLoading } = useQuery({
    queryKey: ['branch-printer-toner-report'],
    queryFn: async () => {
      const response = await api.get('/consumables/branch-report');
      return response.data;
    },
    enabled: activeTab === 'branch-report',
  });

  // Fetch color toner analytics
  const { data: colorAnalyticsData, isLoading: colorAnalyticsLoading } = useQuery({
    queryKey: ['color-toner-analytics'],
    queryFn: async () => {
      const response = await api.get('/consumables/color-analytics');
      return response.data;
    },
    enabled: activeTab === 'color-analytics',
  });

  // Fetch printer toner history
  const { data: printerHistoryData, isLoading: printerHistoryLoading } = useQuery({
    queryKey: ['printer-toner-history'],
    queryFn: async () => {
      const response = await api.get('/consumables/printer-history');
      return response.data;
    },
    enabled: activeTab === 'color-analytics',
  });

  // Fetch reorder recommendations
  const { data: reorderData, isLoading: reorderLoading } = useQuery({
    queryKey: ['reorder-recommendations'],
    queryFn: async () => {
      const response = await api.get('/consumables/reorder-recommendations');
      return response.data;
    },
    enabled: activeTab === 'color-analytics',
  });

  // Fetch usage summary
  const { data: usageSummaryData, isLoading: usageSummaryLoading } = useQuery({
    queryKey: ['usage-summary'],
    queryFn: async () => {
      const response = await api.get('/consumables/usage-summary');
      return response.data;
    },
    enabled: activeTab === 'color-analytics',
  });

  // Fetch printer health status
  const { data: printerHealthData, isLoading: printerHealthLoading } = useQuery({
    queryKey: ['printer-health'],
    queryFn: async () => {
      const response = await api.get('/consumables/printer-health');
      return response.data;
    },
    enabled: activeTab === 'printer-health',
  });

  // Fetch compatible toners for selected printer (for quick issue)
  const { data: compatibleTonersData, isLoading: compatibleTonersLoading } = useQuery({
    queryKey: ['compatible-toners', selectedPrinterForIssue],
    queryFn: async () => {
      const response = await api.get(`/consumables/printer/${selectedPrinterForIssue}/compatible-toners`);
      return response.data;
    },
    enabled: showIssueModal && issueMode === 'quick' && !!selectedPrinterForIssue,
  });

  const items: ConsumableItem[] = itemsData?.data || [];
  const categories: ConsumableCategory[] = categoriesData?.data || [];
  const stockLevels: StockLevel[] = stockData?.data || [];
  const issues: ConsumableIssue[] = issuesData?.data || [];
  const printerModels: PrinterModel[] = printerModelsData?.data || [];
  const allCategories: ConsumableCategory[] = allCategoriesData?.data || [];
  const branches: Branch[] = branchesData?.data || [];
  const departments: any[] = departmentsData?.data || [];
  const vendors: any[] = vendorsData?.data || [];
  const allItems: ConsumableItem[] = allItemsData?.data || [];
  const allStock: StockLevel[] = allStockData?.data || [];
  const printerAssets: any[] = printerAssetsData?.data || [];
  const branchReport = branchReportData?.data || [];
  const colorAnalytics = colorAnalyticsData?.data || [];
  const printerHistory = printerHistoryData?.data || [];
  const reorderRecommendations = reorderData?.data || [];
  const usageSummary = usageSummaryData?.data || null;
  const printerHealth = printerHealthData?.data || [];
  const compatibleToners = compatibleTonersData?.data || null;

  const getPagination = () => {
    switch (activeTab) {
      case 'items':
        return itemsData?.pagination;
      case 'categories':
        return categoriesData?.pagination;
      case 'stock':
        return stockData?.pagination;
      case 'issues':
        return issuesData?.pagination;
      case 'printers':
        return printerModelsData?.pagination;
      default:
        return null;
    }
  };

  const pagination = getPagination();

  const isLoading = () => {
    switch (activeTab) {
      case 'items':
        return itemsLoading;
      case 'categories':
        return categoriesLoading;
      case 'stock':
        return stockLoading;
      case 'issues':
        return issuesLoading;
      case 'printers':
        return printerModelsLoading;
      default:
        return false;
    }
  };

  // Category CRUD mutations
  const createCategoryMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const payload = {
        ...data,
        compatibleAssetIds: data.compatibleAssetIds?.length 
          ? JSON.stringify(data.compatibleAssetIds) 
          : undefined,
      };
      return api.post('/consumables/categories', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-categories'] });
      closeCategoryModal();
      toast({ title: 'Success', description: 'Category created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create category', variant: 'destructive' });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CategoryFormData }) => {
      const payload = {
        ...data,
        compatibleAssetIds: data.compatibleAssetIds?.length 
          ? JSON.stringify(data.compatibleAssetIds) 
          : undefined,
      };
      return api.put(`/consumables/categories/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-categories'] });
      closeCategoryModal();
      toast({ title: 'Success', description: 'Category updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update category', variant: 'destructive' });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/consumables/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-categories'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Category deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete category', variant: 'destructive' });
    },
  });

  const openCreateCategoryModal = () => {
    setEditingCategory(null);
    setCategoryFormData(initialCategoryFormData);
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category: ConsumableCategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || '',
      isColor: (category as any).isColor || false,
      compatiblePrinterModelIds: category.compatiblePrinterModelIds || 
        category.compatiblePrinterModels?.map(p => p.id) || [],
      minStockBlack: (category as any).minStockBlack || 10,
      minStockCyan: (category as any).minStockCyan || 4,
      minStockMagenta: (category as any).minStockMagenta || 4,
      minStockYellow: (category as any).minStockYellow || 4,
    });
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryFormData(initialCategoryFormData);
  };

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data: categoryFormData });
    } else {
      createCategoryMutation.mutate(categoryFormData);
    }
  };

  const confirmDelete = (id: string, type: 'category' | 'item' | 'printer') => {
    setDeletingId(id);
    setDeleteType(type);
    setShowDeleteConfirm(true);
  };

  // Item CRUD mutations
  const createItemMutation = useMutation({
    mutationFn: async (data: ItemFormData) => {
      return api.post('/consumables/items', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumables'] });
      closeItemModal();
      toast({ title: 'Success', description: 'Item created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create item', variant: 'destructive' });
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ItemFormData }) => {
      return api.put(`/consumables/items/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumables'] });
      closeItemModal();
      toast({ title: 'Success', description: 'Item updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update item', variant: 'destructive' });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/consumables/items/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumables'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Item deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete item', variant: 'destructive' });
    },
  });

  const openCreateItemModal = () => {
    setEditingItem(null);
    setItemFormData(initialItemFormData);
    setShowItemModal(true);
  };

  const openEditItemModal = (item: ConsumableItem) => {
    setEditingItem(item);
    setItemFormData({
      name: item.name,
      partNumber: item.partNumber || '',
      description: item.description || '',
      categoryId: item.categoryId || item.category?.id || '',
      compatiblePrinterIds: item.compatiblePrinterIds || item.compatiblePrinters?.map((p: any) => p.id) || [],
      compatibleAssetIds: item.compatibleAssetIds || item.compatibleAssets?.map((a: any) => a.id) || [],
      minimumStock: item.minimumStock,
      unit: item.unit,
      isColorCategory: false,
      colorVariants: [],
    });
    setShowItemModal(true);
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setEditingItem(null);
    setItemFormData(initialItemFormData);
  };

  const handleItemSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data: itemFormData });
    } else if (itemFormData.isColorCategory && itemFormData.colorVariants.length > 0) {
      // Create with color variants
      createItemWithVariantsMutation.mutate(itemFormData);
    } else {
      createItemMutation.mutate(itemFormData);
    }
  };

  // Create item with color variants mutation
  const createItemWithVariantsMutation = useMutation({
    mutationFn: async (data: ItemFormData) => {
      return api.post('/consumables/items/with-variants', {
        name: data.name,
        partNumber: data.partNumber,
        description: data.description,
        categoryId: data.categoryId,
        unitOfMeasure: data.unit.toUpperCase(),
        compatiblePrinters: data.compatiblePrinterIds.length > 0 ? JSON.stringify(data.compatiblePrinterIds) : undefined,
        colorVariants: data.colorVariants,
      });
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['consumable-items'] });
      closeItemModal();
      const count = response.data?.items?.length || 0;
      toast({ title: 'Success', description: `Created ${count} items with color variants` });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to create items', variant: 'destructive' });
    },
  });

  // Stock CRUD mutations
  const receiveStockMutation = useMutation({
    mutationFn: async (data: StockFormData) => {
      return api.post('/consumables/stock', {
        itemId: data.consumableId,
        branchId: data.branchId,
        quantity: data.quantity,
        unitPrice: data.unitPrice || undefined,
        batchNumber: data.batchNumber || undefined,
        purchaseDate: data.purchaseDate ? new Date(data.purchaseDate).toISOString() : undefined,
        invoiceNumber: data.invoiceNumber || undefined,
        supplierId: data.supplierId || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-stock'] });
      queryClient.invalidateQueries({ queryKey: ['consumable-stock-all'] });
      closeStockModal();
      toast({ title: 'Success', description: 'Stock received successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to receive stock', variant: 'destructive' });
    },
  });

  const openStockModal = () => {
    setStockFormData(initialStockFormData);
    setShowStockModal(true);
  };

  const closeStockModal = () => {
    setShowStockModal(false);
    setStockFormData(initialStockFormData);
  };

  const handleStockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    receiveStockMutation.mutate(stockFormData);
  };

  // Issue mutations
  const issueConsumableMutation = useMutation({
    mutationFn: async (data: IssueFormData) => {
      return api.post('/consumables/issue', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-issues'] });
      queryClient.invalidateQueries({ queryKey: ['consumable-stock'] });
      queryClient.invalidateQueries({ queryKey: ['printer-health'] });
      closeIssueModal();
      toast({ title: 'Success', description: 'Consumable issued successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to issue consumable', variant: 'destructive' });
    },
  });

  // Quick issue mutation
  const quickIssueMutation = useMutation({
    mutationFn: async (data: { printerId: string; itemId: string; quantity: number; branchId?: string; departmentId?: string; notes?: string }) => {
      return api.post('/consumables/quick-issue', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consumable-issues'] });
      queryClient.invalidateQueries({ queryKey: ['consumable-stock'] });
      queryClient.invalidateQueries({ queryKey: ['printer-health'] });
      queryClient.invalidateQueries({ queryKey: ['compatible-toners'] });
      closeIssueModal();
      toast({ title: 'Success', description: 'Toner issued to printer successfully' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.error || 'Failed to issue toner', 
        variant: 'destructive' 
      });
    },
  });

  const openIssueModal = () => {
    setIssueFormData(initialIssueFormData);
    setSelectedPrinterForIssue('');
    setIssueMode('quick');
    setShowIssueModal(true);
  };

  const closeIssueModal = () => {
    setShowIssueModal(false);
    setIssueFormData(initialIssueFormData);
    setSelectedPrinterForIssue('');
  };

  const handleIssueSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (issueMode === 'quick' && selectedPrinterForIssue && issueFormData.itemId) {
      quickIssueMutation.mutate({
        printerId: selectedPrinterForIssue,
        itemId: issueFormData.itemId,
        quantity: issueFormData.quantity,
        branchId: issueFormData.branchId || undefined,
        departmentId: issueFormData.departmentId || undefined,
        notes: issueFormData.purpose,
      });
    } else {
      issueConsumableMutation.mutate(issueFormData);
    }
  };

  // Printer Model CRUD mutations
  const createPrinterMutation = useMutation({
    mutationFn: async (data: PrinterModelFormData) => {
      return api.post('/consumables/printer-models', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printer-models'] });
      closePrinterModal();
      toast({ title: 'Success', description: 'Printer model created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create printer model', variant: 'destructive' });
    },
  });

  const updatePrinterMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PrinterModelFormData }) => {
      return api.put(`/consumables/printer-models/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printer-models'] });
      closePrinterModal();
      toast({ title: 'Success', description: 'Printer model updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update printer model', variant: 'destructive' });
    },
  });

  const deletePrinterMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/consumables/printer-models/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['printer-models'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Printer model deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete printer model', variant: 'destructive' });
    },
  });

  const openCreatePrinterModal = () => {
    setEditingPrinter(null);
    setPrinterFormData(initialPrinterModelFormData);
    setShowPrinterModal(true);
  };

  const openEditPrinterModal = (printer: PrinterModel) => {
    setEditingPrinter(printer);
    setPrinterFormData({
      modelNumber: printer.modelNumber,
      modelName: printer.modelName,
      printerType: printer.printerType,
      isColor: printer.isColor,
      colors: (printer as any).colors || [],
      compatibleTonerIds: printer.compatibleTonerIds || [],
    });
    setShowPrinterModal(true);
  };

  const closePrinterModal = () => {
    setShowPrinterModal(false);
    setEditingPrinter(null);
    setPrinterFormData(initialPrinterModelFormData);
  };

  const toggleTonerSelection = (tonerId: string) => {
    setPrinterFormData(prev => ({
      ...prev,
      compatibleTonerIds: prev.compatibleTonerIds.includes(tonerId)
        ? prev.compatibleTonerIds.filter(id => id !== tonerId)
        : [...prev.compatibleTonerIds, tonerId],
    }));
  };

  const toggleColorSelection = (color: string) => {
    setPrinterFormData(prev => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color],
    }));
  };

  const handlePrinterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPrinter) {
      updatePrinterMutation.mutate({ id: editingPrinter.id, data: printerFormData });
    } else {
      createPrinterMutation.mutate(printerFormData);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deletingId) return;
    switch (deleteType) {
      case 'category':
        deleteCategoryMutation.mutate(deletingId);
        break;
      case 'item':
        deleteItemMutation.mutate(deletingId);
        break;
      case 'printer':
        deletePrinterMutation.mutate(deletingId);
        break;
    }
  };

  const toggleAssetSelection = (assetId: string) => {
    setItemFormData(prev => ({
      ...prev,
      compatibleAssetIds: prev.compatibleAssetIds.includes(assetId)
        ? prev.compatibleAssetIds.filter(id => id !== assetId)
        : [...prev.compatibleAssetIds, assetId],
    }));
  };

  // Color badge for toner items
  const getColorBadge = (color?: string) => {
    if (!color || color === 'na') return null;
    
    const colorStyles: Record<string, string> = {
      black: 'bg-gray-900 text-white',
      cyan: 'bg-cyan-500 text-white',
      magenta: 'bg-pink-500 text-white',
      yellow: 'bg-yellow-400 text-black',
      tricolor: 'bg-gradient-to-r from-cyan-500 via-pink-500 to-yellow-500 text-white',
    };
    
    return (
      <span className={cn(
        'px-2 py-0.5 text-xs font-medium rounded-full capitalize',
        colorStyles[color.toLowerCase()] || 'bg-gray-200 text-gray-800'
      )}>
        {color}
      </span>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IN_STOCK':
        return 'bg-green-100 text-green-800';
      case 'LOW_STOCK':
        return 'bg-yellow-100 text-yellow-800';
      case 'OUT_OF_STOCK':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const tabs = [
    { id: 'items' as TabType, label: 'Items', icon: Package },
    { id: 'categories' as TabType, label: 'Categories', icon: FolderOpen },
    { id: 'stock' as TabType, label: 'Stock', icon: Boxes },
    { id: 'issues' as TabType, label: 'Issues', icon: History },
    { id: 'printers' as TabType, label: 'Printer Models', icon: Printer },
    { id: 'printer-health' as TabType, label: 'Printer Health', icon: CheckCircle2 },
    { id: 'branch-report' as TabType, label: 'Branch Report', icon: Building2 },
    { id: 'color-analytics' as TabType, label: 'Color Analytics', icon: Palette },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Consumables</h1>
          <p className="text-muted-foreground">Manage consumable items, categories, and stock levels</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'items' && (
            <PermissionGate permission="consumables.create">
              <Button onClick={openCreateItemModal}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </PermissionGate>
          )}
          {activeTab === 'categories' && (
            <PermissionGate permission="consumables.manage_categories">
              <Button onClick={openCreateCategoryModal}>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </PermissionGate>
          )}
          {activeTab === 'stock' && (
            <PermissionGate permission="consumables.receive_stock">
              <Button onClick={openStockModal}>
                <PackagePlus className="w-4 h-4 mr-2" />
                Receive Stock
              </Button>
            </PermissionGate>
          )}
          {activeTab === 'issues' && (
            <PermissionGate permission="consumables.issue">
              <Button onClick={openIssueModal}>
                <Send className="w-4 h-4 mr-2" />
                Issue Consumable
              </Button>
            </PermissionGate>
          )}
          {activeTab === 'printers' && (
            <PermissionGate permission="consumables.create">
              <Button onClick={openCreatePrinterModal}>
                <Plus className="w-4 h-4 mr-2" />
                Add Printer Model
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearch('');
                setPage(1);
              }}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={`Search ${activeTab}...`}
                className="pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {isLoading() ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Items Tab */}
              {activeTab === 'items' && (
                items.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No consumable items found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Name</th>
                          <th className="text-left p-4 font-medium">Part Number</th>
                          <th className="text-left p-4 font-medium">Category</th>
                          <th className="text-left p-4 font-medium">Quantity</th>
                          <th className="text-left p-4 font-medium">Min. Stock</th>
                          <th className="text-left p-4 font-medium">Status</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {items.map((item) => (
                          <tr key={item.id} className="hover:bg-muted/50">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="font-medium">{item.name}</p>
                                  {item.description && (
                                    <p className="text-sm text-muted-foreground truncate max-w-xs">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                                {getColorBadge(item.color)}
                              </div>
                            </td>
                            <td className="p-4">
                              {item.partNumber ? (
                                <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                                  {item.partNumber}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="p-4">{item.category?.name || '-'}</td>
                            <td className="p-4">
                              <span className="font-medium">{item.quantity}</span>
                              <span className="text-muted-foreground ml-1">{item.unit}</span>
                            </td>
                            <td className="p-4">{item.minimumStock}</td>
                            <td className="p-4">
                              <span className={cn(
                                'px-2 py-1 text-xs font-medium rounded-full',
                                getStatusColor(item.status || 'IN_STOCK')
                              )}>
                                {(item.status || 'IN_STOCK').replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEditItemModal(item)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => confirmDelete(item.id, 'item')}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Categories Tab */}
              {activeTab === 'categories' && (
                categories.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No categories found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="p-4 bg-blue-50 border-b text-sm text-blue-800">
                      <strong>💡 Tip:</strong> Click Edit on a category to set which printers are compatible with this toner type.
                    </div>
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Toner Category</th>
                          <th className="text-left p-4 font-medium">Compatible Printers</th>
                          <th className="text-left p-4 font-medium">Items</th>
                          <th className="text-left p-4 font-medium">Type</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {categories.map((category) => {
                          const compatibleCount = (() => {
                            try {
                              const ids = (category as any).compatibleAssetIds;
                              if (!ids) return 0;
                              const parsed = typeof ids === 'string' ? JSON.parse(ids) : ids;
                              return Array.isArray(parsed) ? parsed.length : 0;
                            } catch { return 0; }
                          })();
                          return (
                            <tr key={category.id} className="hover:bg-muted/50">
                              <td className="p-4">
                                <div className="font-medium">{category.name}</div>
                                {category.description && (
                                  <div className="text-sm text-muted-foreground">{category.description}</div>
                                )}
                              </td>
                              <td className="p-4">
                                {compatibleCount > 0 ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    <Printer className="w-3 h-3" />
                                    {compatibleCount} printer{compatibleCount !== 1 ? 's' : ''}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                    <AlertTriangle className="w-3 h-3" />
                                    Not configured
                                  </span>
                                )}
                              </td>
                              <td className="p-4">{category.itemCount || 0}</td>
                              <td className="p-4">
                                {(category as any).isColor ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                    <span className="flex gap-0.5">
                                      <span className="w-2 h-2 rounded-full bg-gray-900" />
                                      <span className="w-2 h-2 rounded-full bg-cyan-500" />
                                      <span className="w-2 h-2 rounded-full bg-pink-500" />
                                      <span className="w-2 h-2 rounded-full bg-yellow-400" />
                                    </span>
                                    Color
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                    B&W
                                  </span>
                                )}
                              </td>
                              <td className="p-4">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditCategoryModal(category)}
                                    title="Edit category & set compatible printers"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => confirmDelete(category.id, 'category')}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Stock Tab */}
              {activeTab === 'stock' && (
                stockLevels.length === 0 ? (
                  <div className="text-center py-12">
                    <Boxes className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No stock levels found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Item</th>
                          <th className="text-left p-4 font-medium">Batch/Date</th>
                          <th className="text-left p-4 font-medium">Branch</th>
                          <th className="text-left p-4 font-medium">Quantity</th>
                          <th className="text-left p-4 font-medium">Unit Price</th>
                          <th className="text-left p-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {stockLevels.map((stock: any) => (
                          <tr
                            key={stock.id}
                            className={cn(
                              'hover:bg-muted/50',
                              (stock.status || '') === 'LOW_STOCK' && 'bg-yellow-50',
                              (stock.status || '') === 'OUT_OF_STOCK' && 'bg-red-50'
                            )}
                          >
                            <td className="p-4">
                              <div>
                                <p className="font-medium">{stock.consumable?.name || stock.item?.name || '-'}</p>
                                {(stock.item?.partNumber || stock.consumable?.partNumber) && (
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {stock.item?.partNumber || stock.consumable?.partNumber}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm">
                                {stock.batchNumber && (
                                  <p className="font-mono text-xs bg-muted px-1 py-0.5 rounded inline-block">
                                    {stock.batchNumber}
                                  </p>
                                )}
                                {stock.purchaseDate && (
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(stock.purchaseDate).toLocaleDateString()}
                                  </p>
                                )}
                                {stock.invoiceNumber && (
                                  <p className="text-xs text-muted-foreground">
                                    Inv: {stock.invoiceNumber}
                                  </p>
                                )}
                                {!stock.batchNumber && !stock.purchaseDate && (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <div>
                                <p>{stock.branch?.name || '-'}</p>
                                {stock.supplier?.name && (
                                  <p className="text-xs text-muted-foreground">
                                    Supplier: {stock.supplier.name}
                                  </p>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={cn(
                                'font-medium',
                                stock.quantity < (stock.minimumStock || 0) && 'text-red-600',
                                stock.quantity === 0 && 'text-red-600'
                              )}>
                                {stock.quantity}
                              </span>
                            </td>
                            <td className="p-4">
                              {stock.unitPrice ? `$${stock.unitPrice.toFixed(2)}` : '-'}
                            </td>
                            <td className="p-4">
                              <span className={cn(
                                'px-2 py-1 text-xs font-medium rounded-full inline-flex items-center gap-1',
                                getStatusColor(stock.status || (stock.quantity === 0 ? 'OUT_OF_STOCK' : 'IN_STOCK'))
                              )}>
                                {(stock.quantity === 0 || (stock.status || '') === 'OUT_OF_STOCK') && (
                                  <AlertTriangle className="w-3 h-3" />
                                )}
                                {stock.quantity === 0 ? 'OUT OF STOCK' : (stock.status || 'IN_STOCK').replace('_', ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Issues Tab */}
              {activeTab === 'issues' && (
                issues.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No issue history found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Date</th>
                          <th className="text-left p-4 font-medium">Item</th>
                          <th className="text-left p-4 font-medium">Printer</th>
                          <th className="text-left p-4 font-medium">Branch</th>
                          <th className="text-left p-4 font-medium">Qty</th>
                          <th className="text-left p-4 font-medium">Issued By</th>
                          <th className="text-left p-4 font-medium">Purpose</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {issues.map((issue) => (
                          <tr key={issue.id} className="hover:bg-muted/50">
                            <td className="p-4 text-sm text-muted-foreground">
                              {formatDate(issue.createdAt)}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{issue.consumable.name}</span>
                                {getColorBadge(issue.consumable.color)}
                              </div>
                            </td>
                            <td className="p-4">
                              {issue.asset ? (
                                <div className="flex flex-col">
                                  <span className="font-medium text-blue-600">{issue.asset.assetTag}</span>
                                  <span className="text-xs text-muted-foreground">{issue.asset.model || issue.asset.name}</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-4">{issue.stock?.branch?.name || '-'}</td>
                            <td className="p-4">
                              <span className="font-medium">{issue.quantity}</span>
                            </td>
                            <td className="p-4 text-sm">
                              {issue.issuedBy.firstName} {issue.issuedBy.lastName}
                            </td>
                            <td className="p-4 text-muted-foreground max-w-xs truncate text-sm">
                              {issue.purpose || issue.reason || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Printer Models Tab */}
              {activeTab === 'printers' && (
                printerModels.length === 0 ? (
                  <div className="text-center py-12">
                    <Printer className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No printer models found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-4 font-medium">Model Number</th>
                          <th className="text-left p-4 font-medium">Model Name</th>
                          <th className="text-left p-4 font-medium">Printer Type</th>
                          <th className="text-left p-4 font-medium">Color</th>
                          <th className="text-right p-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {printerModels.map((printer) => (
                          <tr key={printer.id} className="hover:bg-muted/50">
                            <td className="p-4">
                              <span className="font-mono text-sm bg-muted px-2 py-1 rounded">
                                {printer.modelNumber}
                              </span>
                            </td>
                            <td className="p-4 font-medium">{printer.modelName}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {(printer.printerType || 'LASER').replace('_', ' ')}
                              </span>
                            </td>
                            <td className="p-4">
                              {printer.isColor ? (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                  Color
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                  B&W
                                </span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditPrinterModal(printer)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => confirmDelete(printer.id, 'printer')}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {/* Branch Report Tab */}
              {activeTab === 'branch-report' && (
                branchReportLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading branch report...</p>
                  </div>
                ) : branchReport.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No branch data available</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {branchReport.map((branch: any) => (
                      <div key={branch.branchId} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{branch.branchName}</h3>
                            {branch.branchCode && (
                              <span className="text-sm text-muted-foreground">Code: {branch.branchCode}</span>
                            )}
                          </div>
                          <div className="flex gap-4 text-sm">
                            <div className="text-center">
                              <span className="font-bold text-blue-600">{branch.printerCount}</span>
                              <p className="text-muted-foreground">Printers</p>
                            </div>
                            <div className="text-center">
                              <span className="font-bold text-green-600">{branch.tonerTypeCount}</span>
                              <p className="text-muted-foreground">Toner Types</p>
                            </div>
                            <div className="text-center">
                              <span className="font-bold text-purple-600">{branch.totalStock}</span>
                              <p className="text-muted-foreground">Total Stock</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Printers */}
                        {branch.printers.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-muted-foreground mb-2">Printers</h4>
                            <div className="flex flex-wrap gap-2">
                              {branch.printers.map((printer: any) => (
                                <span
                                  key={printer.id}
                                  className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                                >
                                  {printer.assetTag} - {printer.model || printer.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Toner Stock */}
                        {branch.tonerTypes.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-2">Toner Stock</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {branch.tonerTypes.map((toner: any) => (
                                <div key={toner.name} className="border rounded p-3 bg-muted/30">
                                  <h5 className="font-medium text-sm mb-2">{toner.name}</h5>
                                  <div className="flex flex-wrap gap-1">
                                    {toner.items.map((item: any) => (
                                      <span
                                        key={item.id}
                                        className={cn(
                                          'px-2 py-0.5 text-xs rounded-full flex items-center gap-1',
                                          item.color === 'black' && 'bg-gray-900 text-white',
                                          item.color === 'cyan' && 'bg-cyan-500 text-white',
                                          item.color === 'magenta' && 'bg-pink-500 text-white',
                                          item.color === 'yellow' && 'bg-yellow-400 text-black',
                                          (!item.color || item.color === 'na') && 'bg-gray-200 text-gray-800'
                                        )}
                                      >
                                        {item.color && item.color !== 'na' ? item.color : item.name}
                                        <span className="font-bold">({item.quantity})</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Printer Health Tab */}
              {activeTab === 'printer-health' && (
                printerHealthLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading printer health data...</p>
                  </div>
                ) : printerHealth.length === 0 ? (
                  <div className="text-center py-12">
                    <Printer className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No printer health data available</p>
                    <p className="text-sm text-muted-foreground mt-2">Issue toners to printers to see health metrics</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    {printerHealth.map((printer: any) => (
                      <div key={printer.printer.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-blue-600">{printer.printer.assetTag}</h3>
                            <p className="text-sm text-muted-foreground">{printer.printer.model || printer.printer.name}</p>
                            <p className="text-xs text-muted-foreground">{printer.printer.branch}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPrinterForIssue(printer.printer.id);
                              setIssueMode('quick');
                              setShowIssueModal(true);
                            }}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Issue
                          </Button>
                        </div>

                        {/* Color Status Grid */}
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          {['black', 'cyan', 'magenta', 'yellow'].map((color) => {
                            const stats = printer.colorStats[color];
                            const prediction = printer.predictions[color];
                            const colorStyles: Record<string, string> = {
                              black: 'bg-gray-900',
                              cyan: 'bg-cyan-500',
                              magenta: 'bg-pink-500',
                              yellow: 'bg-yellow-400',
                            };
                            
                            return (
                              <div 
                                key={color} 
                                className={cn(
                                  'p-2 rounded text-center border',
                                  prediction?.urgency === 'HIGH' && 'border-red-400 bg-red-50',
                                  prediction?.urgency === 'MEDIUM' && 'border-yellow-400 bg-yellow-50',
                                  prediction?.urgency === 'LOW' && 'border-green-300 bg-green-50',
                                  !prediction?.urgency && 'border-gray-200'
                                )}
                              >
                                <div className={cn('w-4 h-4 rounded-full mx-auto mb-1', colorStyles[color])} />
                                <p className="text-lg font-bold">{stats?.count || 0}</p>
                                <p className="text-xs text-muted-foreground capitalize">{color}</p>
                                {prediction?.daysUntilNext !== null && prediction?.daysUntilNext !== undefined && (
                                  <p className={cn(
                                    'text-xs mt-1',
                                    prediction.urgency === 'HIGH' && 'text-red-600 font-medium',
                                    prediction.urgency === 'MEDIUM' && 'text-yellow-600',
                                    prediction.urgency === 'LOW' && 'text-green-600'
                                  )}>
                                    ~{prediction.daysUntilNext}d
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Quick Stats */}
                        <div className="flex items-center justify-between text-sm border-t pt-2">
                          <span className="text-muted-foreground">Total replacements:</span>
                          <span className="font-medium">{printer.totalReplacements}</span>
                        </div>
                        {printer.lastReplacement && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Last replacement:</span>
                            <span className="text-xs">
                              {new Date(printer.lastReplacement).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        )}
                        {printer.compatibleToners.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-muted-foreground">Compatible: {printer.compatibleToners.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* Color Analytics Tab */}
              {activeTab === 'color-analytics' && (
                colorAnalyticsLoading || printerHistoryLoading || reorderLoading || usageSummaryLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading color analytics...</p>
                  </div>
                ) : (
                  <div className="space-y-6 p-4">
                    {/* Usage Summary */}
                    {usageSummary && (
                      <div className="border rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <BarChart3 className="w-5 h-5" />
                          Color Usage Summary
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                          <div className="text-center p-3 bg-muted/30 rounded-lg">
                            <p className="text-2xl font-bold">{usageSummary.totalIssued || 0}</p>
                            <p className="text-sm text-muted-foreground">Total Issued</p>
                          </div>
                          {Object.entries(usageSummary.byColor || {}).map(([color, data]: [string, any]) => (
                            <div 
                              key={color}
                              className={cn(
                                'text-center p-3 rounded-lg',
                                color === 'black' && 'bg-gray-900 text-white',
                                color === 'cyan' && 'bg-cyan-500 text-white',
                                color === 'magenta' && 'bg-pink-500 text-white',
                                color === 'yellow' && 'bg-yellow-400 text-black',
                                color === 'other' && 'bg-gray-200 text-gray-800'
                              )}
                            >
                              <p className="text-2xl font-bold">{data.count}</p>
                              <p className="text-sm opacity-90 capitalize">{color} ({data.percentage}%)</p>
                            </div>
                          ))}
                        </div>
                        {usageSummary.insights && usageSummary.insights.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <h4 className="font-medium text-blue-800 mb-2">Insights</h4>
                            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                              {usageSummary.insights.map((insight: string, i: number) => (
                                <li key={i}>{insight}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Color Stock Status by Category */}
                    <div className="border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Color Stock Status by Category
                      </h3>
                      {colorAnalytics.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No color toner categories found. Mark a category as "Color" to enable analytics.</p>
                      ) : (
                        <div className="space-y-6">
                          {colorAnalytics.map((category: any) => (
                            <div key={category.categoryId} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold">{category.categoryName}</h4>
                                <div className="text-xs text-muted-foreground">
                                  Min Levels: 
                                  <span className="ml-1 px-1 bg-gray-900 text-white rounded">B:{category.minLevels.black}</span>
                                  <span className="ml-1 px-1 bg-cyan-500 text-white rounded">C:{category.minLevels.cyan}</span>
                                  <span className="ml-1 px-1 bg-pink-500 text-white rounded">M:{category.minLevels.magenta}</span>
                                  <span className="ml-1 px-1 bg-yellow-400 text-black rounded">Y:{category.minLevels.yellow}</span>
                                </div>
                              </div>
                              {category.branches.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No stock data available</p>
                              ) : (
                                <div className="space-y-3">
                                  {category.branches.map((branch: any) => (
                                    <div key={branch.branchId} className="bg-muted/30 rounded-lg p-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium">{branch.branchName}</span>
                                        <span className={cn(
                                          'px-2 py-0.5 text-xs font-medium rounded-full',
                                          branch.overallStatus === 'OK' && 'bg-green-100 text-green-800',
                                          branch.overallStatus === 'LOW_STOCK' && 'bg-yellow-100 text-yellow-800',
                                          branch.overallStatus === 'INCOMPLETE' && 'bg-red-100 text-red-800'
                                        )}>
                                          {branch.overallStatus === 'OK' ? '✓ Complete' : 
                                           branch.overallStatus === 'LOW_STOCK' ? '⚠ Low Stock' : 
                                           '✗ Incomplete Set'}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-4 gap-2">
                                        {branch.colors.map((color: any) => (
                                          <div 
                                            key={color.color}
                                            className={cn(
                                              'p-2 rounded text-center',
                                              color.status === 'OUT_OF_STOCK' && 'bg-red-100 border-2 border-red-400',
                                              color.status === 'LOW_STOCK' && 'bg-yellow-100 border-2 border-yellow-400',
                                              color.status === 'IN_STOCK' && 'bg-green-100 border border-green-300'
                                            )}
                                          >
                                            <div className={cn(
                                              'w-4 h-4 rounded-full mx-auto mb-1',
                                              color.color === 'black' && 'bg-gray-900',
                                              color.color === 'cyan' && 'bg-cyan-500',
                                              color.color === 'magenta' && 'bg-pink-500',
                                              color.color === 'yellow' && 'bg-yellow-400'
                                            )} />
                                            <p className="font-bold text-lg">{color.quantity}</p>
                                            <p className="text-xs text-muted-foreground capitalize">{color.color}</p>
                                            <p className="text-xs text-muted-foreground">min: {color.minRequired}</p>
                                            {color.deficit > 0 && (
                                              <p className="text-xs text-red-600 font-medium">Need +{color.deficit}</p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                      {branch.missingColors.length > 0 && (
                                        <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                          <AlertTriangle className="w-4 h-4" />
                                          Missing: {branch.missingColors.join(', ')}
                                        </div>
                                      )}
                                      <div className="mt-2 text-sm text-muted-foreground">
                                        Complete sets available: <span className="font-bold">{branch.completeSets}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reorder Recommendations */}
                    <div className="border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" />
                        Reorder Recommendations
                      </h3>
                      {reorderRecommendations.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                          <p className="text-muted-foreground">All stock levels are healthy. No reorders needed!</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {reorderRecommendations.map((rec: any, i: number) => (
                            <div key={i} className="bg-muted/30 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <span className="font-medium">{rec.categoryName}</span>
                                  <span className="text-sm text-muted-foreground ml-2">@ {rec.branchName}</span>
                                </div>
                                <span className="text-sm font-medium text-blue-600">
                                  Order {rec.totalItemsToOrder} items
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {rec.items.map((item: any) => (
                                  <div 
                                    key={item.color}
                                    className={cn(
                                      'px-3 py-1 rounded-full text-sm flex items-center gap-2',
                                      item.priority === 'URGENT' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                                    )}
                                  >
                                    <span className={cn(
                                      'w-3 h-3 rounded-full',
                                      item.color === 'black' && 'bg-gray-900',
                                      item.color === 'cyan' && 'bg-cyan-500',
                                      item.color === 'magenta' && 'bg-pink-500',
                                      item.color === 'yellow' && 'bg-yellow-400'
                                    )} />
                                    <span className="capitalize">{item.color}:</span>
                                    <span className="font-medium">+{item.suggestedOrder}</span>
                                    {item.priority === 'URGENT' && (
                                      <AlertTriangle className="w-3 h-3" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Printer Toner History */}
                    <div className="border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Printer Toner Replacement History & Predictions
                      </h3>
                      {printerHistory.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No toner replacement history found. Issue toners to printers to see analytics.</p>
                      ) : (
                        <div className="space-y-4">
                          {printerHistory.map((printer: any) => (
                            <div key={printer.printer.id} className="bg-muted/30 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <span className="font-medium">{printer.printer.assetTag}</span>
                                  <span className="text-sm text-muted-foreground ml-2">
                                    {printer.printer.model || printer.printer.name}
                                  </span>
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({printer.printer.branch})
                                  </span>
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  Total: {printer.totalReplacements} replacements
                                </span>
                              </div>
                              
                              {/* Color replacement counts */}
                              <div className="grid grid-cols-4 gap-2 mb-3">
                                {Object.entries(printer.colorReplacements).filter(([c]) => c !== 'other').map(([color, count]: [string, any]) => (
                                  <div key={color} className="text-center p-2 bg-background rounded">
                                    <div className={cn(
                                      'w-4 h-4 rounded-full mx-auto mb-1',
                                      color === 'black' && 'bg-gray-900',
                                      color === 'cyan' && 'bg-cyan-500',
                                      color === 'magenta' && 'bg-pink-500',
                                      color === 'yellow' && 'bg-yellow-400'
                                    )} />
                                    <p className="font-bold">{count}</p>
                                    <p className="text-xs text-muted-foreground capitalize">{color}</p>
                                  </div>
                                ))}
                              </div>

                              {/* Predictions */}
                              {printer.predictions && (
                                <div className="border-t pt-3">
                                  <p className="text-xs font-medium text-muted-foreground mb-2">Predicted Next Replacement:</p>
                                  <div className="grid grid-cols-4 gap-2">
                                    {Object.entries(printer.predictions).map(([color, pred]: [string, any]) => (
                                      <div 
                                        key={color}
                                        className={cn(
                                          'text-center p-2 rounded text-xs',
                                          pred.urgency === 'HIGH' && 'bg-red-100',
                                          pred.urgency === 'MEDIUM' && 'bg-yellow-100',
                                          pred.urgency === 'LOW' && 'bg-green-100',
                                          !pred.urgency && 'bg-gray-100'
                                        )}
                                      >
                                        <span className="capitalize font-medium">{color}</span>
                                        {pred.estimatedDaysUntilNext !== undefined ? (
                                          <>
                                            <p className="font-bold">{pred.estimatedDaysUntilNext} days</p>
                                            <p className="text-muted-foreground">avg: {pred.avgDaysBetweenReplacements}d</p>
                                          </>
                                        ) : (
                                          <p className="text-muted-foreground">No data</p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Recent issues */}
                              {printer.recentIssues && printer.recentIssues.length > 0 && (
                                <div className="border-t pt-3 mt-3">
                                  <p className="text-xs font-medium text-muted-foreground mb-2">Recent Replacements:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {printer.recentIssues.slice(0, 5).map((issue: any, i: number) => (
                                      <span 
                                        key={i}
                                        className={cn(
                                          'px-2 py-0.5 text-xs rounded-full',
                                          issue.color?.toLowerCase() === 'black' && 'bg-gray-900 text-white',
                                          issue.color?.toLowerCase() === 'cyan' && 'bg-cyan-500 text-white',
                                          issue.color?.toLowerCase() === 'magenta' && 'bg-pink-500 text-white',
                                          issue.color?.toLowerCase() === 'yellow' && 'bg-yellow-400 text-black',
                                          (!issue.color || issue.color === 'na') && 'bg-gray-200'
                                        )}
                                      >
                                        {new Date(issue.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">
                Page {page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Category Create/Edit Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
          <Card className="w-full max-w-lg mx-4 my-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingCategory ? 'Edit Category' : 'Create Category'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeCategoryModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={categoryFormData.name}
                    onChange={(e) =>
                      setCategoryFormData({ ...categoryFormData, name: e.target.value })
                    }
                    required
                    placeholder="e.g., HP 963"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    value={categoryFormData.description}
                    onChange={(e) =>
                      setCategoryFormData({ ...categoryFormData, description: e.target.value })
                    }
                    className="w-full min-h-[60px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    placeholder="Optional description..."
                  />
                </div>
                
                {/* Is Color Category Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isColor"
                    checked={categoryFormData.isColor || false}
                    onChange={(e) =>
                      setCategoryFormData({ ...categoryFormData, isColor: e.target.checked })
                    }
                    className="w-4 h-4 rounded"
                  />
                  <Label htmlFor="isColor" className="cursor-pointer">
                    This is a color toner category (CMYK)
                  </Label>
                </div>

                {/* Per-Color Minimum Stock Levels - Only show for color categories */}
                {categoryFormData.isColor && (
                  <div className="border border-input rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Palette className="w-4 h-4" />
                      <Label className="font-semibold">Per-Color Minimum Stock Levels</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">
                      Set different minimum stock levels for each color. Black typically needs higher levels due to frequent usage.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2 mb-1">
                          <span className="w-3 h-3 rounded-full bg-gray-900" />
                          Black (High Usage)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={categoryFormData.minStockBlack || 10}
                          onChange={(e) =>
                            setCategoryFormData({ ...categoryFormData, minStockBlack: parseInt(e.target.value) || 0 })
                          }
                          placeholder="10"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 mb-1">
                          <span className="w-3 h-3 rounded-full bg-cyan-500" />
                          Cyan
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={categoryFormData.minStockCyan || 4}
                          onChange={(e) =>
                            setCategoryFormData({ ...categoryFormData, minStockCyan: parseInt(e.target.value) || 0 })
                          }
                          placeholder="4"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 mb-1">
                          <span className="w-3 h-3 rounded-full bg-pink-500" />
                          Magenta
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={categoryFormData.minStockMagenta || 4}
                          onChange={(e) =>
                            setCategoryFormData({ ...categoryFormData, minStockMagenta: parseInt(e.target.value) || 0 })
                          }
                          placeholder="4"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2 mb-1">
                          <span className="w-3 h-3 rounded-full bg-yellow-400" />
                          Yellow
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={categoryFormData.minStockYellow || 4}
                          onChange={(e) =>
                            setCategoryFormData({ ...categoryFormData, minStockYellow: parseInt(e.target.value) || 0 })
                          }
                          placeholder="4"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 italic">
                      Tip: Black typically runs out 2-3x faster than colors. Recommended ratio: Black 10, Colors 4 each.
                    </p>
                  </div>
                )}

                <div>
                  <Label>Compatible Printers (Assets)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which printers are compatible with this toner category
                  </p>
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                    {printerAssets.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No printer assets found.</p>
                    ) : (
                      printerAssets.map((printer: any) => (
                        <label
                          key={printer.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-accent cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={categoryFormData.compatibleAssetIds?.includes(printer.id) || false}
                            onChange={(e) => {
                              const currentIds = categoryFormData.compatibleAssetIds || [];
                              if (e.target.checked) {
                                setCategoryFormData({
                                  ...categoryFormData,
                                  compatibleAssetIds: [...currentIds, printer.id],
                                });
                              } else {
                                setCategoryFormData({
                                  ...categoryFormData,
                                  compatibleAssetIds: currentIds.filter((id) => id !== printer.id),
                                });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{printer.assetTag} - {printer.name}</span>
                            <span className="text-xs text-muted-foreground">{printer.model} • {printer.branch?.name || 'No branch'}</span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeCategoryModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
                  >
                    {editingCategory ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this {deleteType}? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteConfirm}
                  loading={
                    deleteCategoryMutation.isPending || 
                    deleteItemMutation.isPending || 
                    deletePrinterMutation.isPending
                  }
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Item Create/Edit Modal */}
      {showItemModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
          <Card className="w-full max-w-2xl mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingItem ? 'Edit Item' : 'Create Consumable Item'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closeItemModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleItemSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="item-name">Name *</Label>
                    <Input
                      id="item-name"
                      value={itemFormData.name}
                      onChange={(e) =>
                        setItemFormData({ ...itemFormData, name: e.target.value })
                      }
                      required
                      placeholder="e.g., HP 963"
                    />
                  </div>
                  <div>
                    <Label htmlFor="item-partNumber">Part Number</Label>
                    <Input
                      id="item-partNumber"
                      value={itemFormData.partNumber}
                      onChange={(e) =>
                        setItemFormData({ ...itemFormData, partNumber: e.target.value })
                      }
                      placeholder="e.g., CF226A"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="item-description">Description</Label>
                  <textarea
                    id="item-description"
                    value={itemFormData.description}
                    onChange={(e) =>
                      setItemFormData({ ...itemFormData, description: e.target.value })
                    }
                    className="w-full min-h-[80px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    placeholder="Optional description..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="item-category">Category *</Label>
                    <select
                      id="item-category"
                      value={itemFormData.categoryId}
                      onChange={(e) => {
                        const selectedCat = allCategories.find(c => c.id === e.target.value);
                        setItemFormData({ 
                          ...itemFormData, 
                          categoryId: e.target.value,
                          isColorCategory: selectedCat?.isColor || false,
                          colorVariants: selectedCat?.isColor && !editingItem ? [
                            { color: 'black', minimumStock: 1 },
                            { color: 'cyan', minimumStock: 1 },
                            { color: 'magenta', minimumStock: 1 },
                            { color: 'yellow', minimumStock: 1 },
                          ] : []
                        });
                      }}
                      required
                      className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="">Select a category...</option>
                      {allCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name} {cat.isColor && '🎨'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="item-unit">Unit of Measure *</Label>
                    <select
                      id="item-unit"
                      value={itemFormData.unit}
                      onChange={(e) =>
                        setItemFormData({ ...itemFormData, unit: e.target.value })
                      }
                      required
                      className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                    >
                      <option value="pcs">Pieces (pcs)</option>
                      <option value="box">Box</option>
                      <option value="pack">Pack</option>
                      <option value="ream">Ream</option>
                      <option value="roll">Roll</option>
                      <option value="bottle">Bottle</option>
                      <option value="set">Set</option>
                    </select>
                  </div>
                </div>

                {/* Color Variants Section - for color categories */}
                {itemFormData.isColorCategory && !editingItem && (
                  <div className="border border-input rounded-md p-4 bg-muted/30">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex gap-1">
                        <span className="w-4 h-4 rounded-full bg-gray-900" />
                        <span className="w-4 h-4 rounded-full bg-cyan-500" />
                        <span className="w-4 h-4 rounded-full bg-pink-500" />
                        <span className="w-4 h-4 rounded-full bg-yellow-400" />
                      </div>
                      <Label className="text-sm font-semibold">Color Variants</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      This category supports color variants. The system will create separate items for each color.
                    </p>
                    <div className="space-y-3">
                      {['black', 'cyan', 'magenta', 'yellow'].map((color) => {
                        const variant = itemFormData.colorVariants.find(v => v.color === color);
                        const isEnabled = !!variant;
                        const colorStyles: Record<string, string> = {
                          black: 'bg-gray-900',
                          cyan: 'bg-cyan-500',
                          magenta: 'bg-pink-500',
                          yellow: 'bg-yellow-400',
                        };
                        return (
                          <div key={color} className="flex items-center gap-4">
                            <label className="flex items-center gap-2 w-32">
                              <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setItemFormData({
                                      ...itemFormData,
                                      colorVariants: [...itemFormData.colorVariants, { color, minimumStock: 1 }]
                                    });
                                  } else {
                                    setItemFormData({
                                      ...itemFormData,
                                      colorVariants: itemFormData.colorVariants.filter(v => v.color !== color)
                                    });
                                  }
                                }}
                                className="w-4 h-4"
                              />
                              <span className={cn('w-4 h-4 rounded-full', colorStyles[color])} />
                              <span className="capitalize text-sm">{color}</span>
                            </label>
                            {isEnabled && (
                              <div className="flex items-center gap-2">
                                <Label className="text-xs text-muted-foreground">Min Stock:</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={variant?.minimumStock || 1}
                                  onChange={(e) => {
                                    setItemFormData({
                                      ...itemFormData,
                                      colorVariants: itemFormData.colorVariants.map(v =>
                                        v.color === color ? { ...v, minimumStock: parseInt(e.target.value) || 0 } : v
                                      )
                                    });
                                  }}
                                  className="w-20 h-8"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Single item minimum stock - only for non-color or editing */}
                {(!itemFormData.isColorCategory || editingItem) && (
                  <div>
                    <Label htmlFor="item-minimumStock">Minimum Stock Level *</Label>
                    <Input
                      id="item-minimumStock"
                      type="number"
                      min="0"
                      value={itemFormData.minimumStock}
                      onChange={(e) =>
                        setItemFormData({ ...itemFormData, minimumStock: parseInt(e.target.value) || 0 })
                      }
                      required
                      placeholder="e.g., 10"
                    />
                  </div>
                )}

                <div>
                  <Label>Compatible Printers (Assets)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Select the actual printers this consumable is compatible with</p>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-input rounded-md p-2">
                    {printerAssets.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">No printer assets found</p>
                    ) : (
                      <div className="grid grid-cols-1 gap-2">
                        {printerAssets.map((printer: any) => (
                          <label
                            key={printer.id}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 border border-transparent',
                              itemFormData.compatibleAssetIds.includes(printer.id) && 'bg-primary/10 border-primary/30'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={itemFormData.compatibleAssetIds.includes(printer.id)}
                              onChange={() => toggleAssetSelection(printer.id)}
                              className="w-4 h-4"
                            />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {printer.assetTag} - {printer.name || printer.model}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {printer.model} • {printer.branch?.name || 'No branch'}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeItemModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createItemMutation.isPending || updateItemMutation.isPending}
                  >
                    {createItemMutation.isPending || updateItemMutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {editingItem ? 'Updating...' : 'Creating...'}
                      </span>
                    ) : (
                      <>
                        {editingItem ? 'Update' : itemFormData.isColorCategory && itemFormData.colorVariants.length > 0 
                          ? `Create ${itemFormData.colorVariants.length + 1} Items` 
                          : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Receive Stock Modal */}
      {showStockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
          <Card className="w-full max-w-lg mx-4 my-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Receive Stock</h2>
                <Button variant="ghost" size="icon" onClick={closeStockModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handleStockSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="stock-item">Item *</Label>
                  <select
                    id="stock-item"
                    value={stockFormData.consumableId}
                    onChange={(e) =>
                      setStockFormData({ ...stockFormData, consumableId: e.target.value })
                    }
                    required
                    className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select an item...</option>
                    {allItems.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} {item.partNumber && `(${item.partNumber})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="stock-branch">Storage Location (Branch) *</Label>
                  <select
                    id="stock-branch"
                    value={stockFormData.branchId}
                    onChange={(e) =>
                      setStockFormData({ ...stockFormData, branchId: e.target.value })
                    }
                    required
                    className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">Select branch (usually Head Office)...</option>
                    {branches.map((branch: any) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.isHeadquarters && '(Head Office)'} {branch.code && `- ${branch.code}`}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Toners are typically stored at Head Office
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="stock-quantity">Quantity *</Label>
                    <Input
                      id="stock-quantity"
                      type="number"
                      min="1"
                      value={stockFormData.quantity || ''}
                      onChange={(e) =>
                        setStockFormData({ ...stockFormData, quantity: parseInt(e.target.value) || 0 })
                      }
                      required
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock-unitPrice">Unit Price</Label>
                    <Input
                      id="stock-unitPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={stockFormData.unitPrice || ''}
                      onChange={(e) =>
                        setStockFormData({ ...stockFormData, unitPrice: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="e.g., 25.99"
                    />
                  </div>
                </div>

                {/* Batch Tracking Section */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <h3 className="text-sm font-semibold mb-3">Batch Tracking</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="stock-batchNumber">Batch Number</Label>
                      <Input
                        id="stock-batchNumber"
                        value={stockFormData.batchNumber}
                        onChange={(e) =>
                          setStockFormData({ ...stockFormData, batchNumber: e.target.value })
                        }
                        placeholder="e.g., BATCH-2026-001"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stock-purchaseDate">Stock Date *</Label>
                      <Input
                        id="stock-purchaseDate"
                        type="date"
                        value={stockFormData.purchaseDate}
                        onChange={(e) =>
                          setStockFormData({ ...stockFormData, purchaseDate: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label htmlFor="stock-invoiceNumber">Invoice Number</Label>
                      <Input
                        id="stock-invoiceNumber"
                        value={stockFormData.invoiceNumber}
                        onChange={(e) =>
                          setStockFormData({ ...stockFormData, invoiceNumber: e.target.value })
                        }
                        placeholder="e.g., INV-12345"
                      />
                    </div>
                    <div>
                      <Label htmlFor="stock-supplier">Supplier</Label>
                      <select
                        id="stock-supplier"
                        value={stockFormData.supplierId}
                        onChange={(e) =>
                          setStockFormData({ ...stockFormData, supplierId: e.target.value })
                        }
                        className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">Select supplier...</option>
                        {vendors.map((vendor: any) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closeStockModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={receiveStockMutation.isPending}>
                    {receiveStockMutation.isPending ? 'Receiving...' : 'Receive Stock'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Issue Consumable Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
          <Card className="w-full max-w-lg mx-4 my-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Issue Toner</h2>
                <Button variant="ghost" size="icon" onClick={closeIssueModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2 mb-4 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => setIssueMode('quick')}
                  className={cn(
                    'flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors',
                    issueMode === 'quick' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Printer className="w-4 h-4 inline mr-2" />
                  Quick Issue (Printer-First)
                </button>
                <button
                  type="button"
                  onClick={() => setIssueMode('standard')}
                  className={cn(
                    'flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors',
                    issueMode === 'standard' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Package className="w-4 h-4 inline mr-2" />
                  Standard (Stock-First)
                </button>
              </div>

              <form onSubmit={handleIssueSubmit} className="space-y-4">
                {issueMode === 'quick' ? (
                  <>
                    {/* Quick Issue Mode - Printer First */}
                    <div>
                      <Label htmlFor="quick-printer">1. Select Printer *</Label>
                      <select
                        id="quick-printer"
                        value={selectedPrinterForIssue}
                        onChange={(e) => {
                          setSelectedPrinterForIssue(e.target.value);
                          setIssueFormData({ ...issueFormData, itemId: '' });
                        }}
                        required
                        disabled={printerAssetsLoading}
                        className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">{printerAssetsLoading ? 'Loading printers...' : 'Select a printer...'}</option>
                        {printerAssets.map((printer) => (
                          <option key={printer.id} value={printer.id}>
                            {printer.assetTag} - {printer.model || printer.name} ({printer.branch?.name || 'No Branch'})
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedPrinterForIssue && (
                      <>
                        {compatibleTonersLoading ? (
                          <div className="text-center py-4">
                            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">Loading compatible toners...</p>
                          </div>
                        ) : compatibleToners?.compatibleToners?.length > 0 ? (
                          <div>
                            <Label>2. Select Toner *</Label>
                            {compatibleToners?.message && (
                              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded mb-2">
                                ℹ️ {compatibleToners.message}
                                {compatibleToners?.headquarters && (
                                  <span className="font-medium ml-1">({compatibleToners.headquarters.name})</span>
                                )}
                              </div>
                            )}
                            <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
                              {compatibleToners.compatibleToners.map((toner: any) => (
                                <label
                                  key={toner.id}
                                  className={cn(
                                    'flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted/50 border',
                                    issueFormData.itemId === toner.id 
                                      ? 'bg-primary/10 border-primary' 
                                      : 'border-transparent',
                                    toner.totalStock === 0 && 'opacity-50 cursor-not-allowed'
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name="quick-toner"
                                      checked={issueFormData.itemId === toner.id}
                                      onChange={() => setIssueFormData({ ...issueFormData, itemId: toner.id })}
                                      disabled={toner.totalStock === 0}
                                      className="w-4 h-4"
                                    />
                                    <div>
                                      <span className="text-sm font-medium">{toner.name}</span>
                                      {toner.color && toner.color !== 'na' && (
                                        <span className={cn(
                                          'ml-2 px-2 py-0.5 text-xs rounded-full',
                                          toner.color?.toLowerCase() === 'black' && 'bg-gray-900 text-white',
                                          toner.color?.toLowerCase() === 'cyan' && 'bg-cyan-500 text-white',
                                          toner.color?.toLowerCase() === 'magenta' && 'bg-pink-500 text-white',
                                          toner.color?.toLowerCase() === 'yellow' && 'bg-yellow-400 text-black'
                                        )}>
                                          {toner.color}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <span className={cn(
                                      'text-sm font-medium',
                                      toner.totalStock === 0 && 'text-red-600',
                                      toner.totalStock > 0 && toner.totalStock <= 2 && 'text-yellow-600',
                                      toner.totalStock > 2 && 'text-green-600'
                                    )}>
                                      {toner.totalStock === 0 ? 'Out of Stock' : `Stock: ${toner.totalStock}`}
                                    </span>
                                    {toner.headquartersStock !== undefined && toner.headquartersStock !== toner.totalStock && (
                                      <p className="text-xs text-muted-foreground">HQ: {toner.headquartersStock}</p>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-4 border rounded-md bg-yellow-50">
                            <AlertTriangle className="w-8 h-8 mx-auto text-yellow-600 mb-2" />
                            <p className="text-sm text-yellow-800">No toners found.</p>
                            <p className="text-xs text-yellow-600 mt-1">
                              Add stock at Head Office first using the Stock tab.
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Issue To Section */}
                    <div className="border rounded-lg p-3 bg-muted/30">
                      <Label className="text-sm font-semibold">3. Issue To</Label>
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label htmlFor="quick-branch" className="text-xs">Branch *</Label>
                          <select
                            id="quick-branch"
                            value={issueFormData.branchId}
                            onChange={(e) =>
                              setIssueFormData({ ...issueFormData, branchId: e.target.value })
                            }
                            required
                            className="w-full h-9 px-2 py-1 mt-1 rounded-md border border-input bg-background text-sm"
                          >
                            <option value="">Select branch...</option>
                            {branches.map((branch: any) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="quick-department" className="text-xs">Department</Label>
                          <select
                            id="quick-department"
                            value={issueFormData.departmentId}
                            onChange={(e) =>
                              setIssueFormData({ ...issueFormData, departmentId: e.target.value })
                            }
                            className="w-full h-9 px-2 py-1 mt-1 rounded-md border border-input bg-background text-sm"
                          >
                            <option value="">Select department...</option>
                            {departments.map((dept: any) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quick-quantity">4. Quantity *</Label>
                        <Input
                          id="quick-quantity"
                          type="number"
                          min="1"
                          value={issueFormData.quantity || 1}
                          onChange={(e) =>
                            setIssueFormData({ ...issueFormData, quantity: parseInt(e.target.value) || 1 })
                          }
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="quick-notes">Notes (Optional)</Label>
                        <Input
                          id="quick-notes"
                          value={issueFormData.purpose}
                          onChange={(e) =>
                            setIssueFormData({ ...issueFormData, purpose: e.target.value })
                          }
                          placeholder="e.g., Regular replacement"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Standard Issue Mode */}
                    <div>
                      <Label htmlFor="issue-stock">Stock Record (Item @ Branch) *</Label>
                      <select
                        id="issue-stock"
                        value={issueFormData.stockId}
                        onChange={(e) => {
                          const selectedStock = allStock.find(s => s.id === e.target.value);
                          setIssueFormData({ 
                            ...issueFormData, 
                            stockId: e.target.value,
                            itemId: selectedStock?.consumable?.id || selectedStock?.item?.id || ''
                          });
                        }}
                        required
                        className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">Select stock record...</option>
                        {allStock
                          .filter(s => s.quantity > 0)
                          .map((stock) => (
                            <option key={stock.id} value={stock.id}>
                              {stock.consumable?.name || stock.item?.name || 'Unknown'} @ {stock.branch?.name || 'Unknown'} (Qty: {stock.quantity})
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="issue-quantity">Quantity *</Label>
                      <Input
                        id="issue-quantity"
                        type="number"
                        min="1"
                        value={issueFormData.quantity || ''}
                        onChange={(e) =>
                          setIssueFormData({ ...issueFormData, quantity: parseInt(e.target.value) || 1 })
                        }
                        required
                        placeholder="e.g., 1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="issue-printer">Issue To Printer *</Label>
                      <select
                        id="issue-printer"
                        value={issueFormData.printerId}
                        onChange={(e) =>
                          setIssueFormData({ ...issueFormData, printerId: e.target.value })
                        }
                        required
                        disabled={printerAssetsLoading}
                        className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="">{printerAssetsLoading ? 'Loading printers...' : `Select printer...`}</option>
                        {printerAssets.map((printer) => (
                          <option key={printer.id} value={printer.id}>
                            {printer.assetTag || printer.name} - {printer.branch?.name || 'No Branch'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="issue-purpose">Purpose / Notes</Label>
                      <textarea
                        id="issue-purpose"
                        value={issueFormData.purpose}
                        onChange={(e) =>
                          setIssueFormData({ ...issueFormData, purpose: e.target.value })
                        }
                        className="w-full min-h-[60px] px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                        placeholder="e.g., Toner replacement..."
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={closeIssueModal}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={
                      issueConsumableMutation.isPending || 
                      quickIssueMutation.isPending ||
                      (issueMode === 'quick' && (!selectedPrinterForIssue || !issueFormData.itemId))
                    }
                  >
                    {(issueConsumableMutation.isPending || quickIssueMutation.isPending) ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Issuing...
                      </span>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Issue Toner
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Printer Model Create/Edit Modal */}
      {showPrinterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {editingPrinter ? 'Edit Printer Model' : 'Create Printer Model'}
                </h2>
                <Button variant="ghost" size="icon" onClick={closePrinterModal}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <form onSubmit={handlePrinterSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="printer-modelNumber">Model Number *</Label>
                  <Input
                    id="printer-modelNumber"
                    value={printerFormData.modelNumber}
                    onChange={(e) =>
                      setPrinterFormData({ ...printerFormData, modelNumber: e.target.value })
                    }
                    required
                    placeholder="e.g., HP-M404dn"
                  />
                </div>
                <div>
                  <Label htmlFor="printer-modelName">Model Name *</Label>
                  <Input
                    id="printer-modelName"
                    value={printerFormData.modelName}
                    onChange={(e) =>
                      setPrinterFormData({ ...printerFormData, modelName: e.target.value })
                    }
                    required
                    placeholder="e.g., HP LaserJet Pro M404dn"
                  />
                </div>
                <div>
                  <Label htmlFor="printer-type">Printer Type *</Label>
                  <select
                    id="printer-type"
                    value={printerFormData.printerType}
                    onChange={(e) =>
                      setPrinterFormData({ 
                        ...printerFormData, 
                        printerType: e.target.value as PrinterModelFormData['printerType'] 
                      })
                    }
                    required
                    className="w-full h-10 px-3 py-2 mt-1 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="LASER">Laser</option>
                    <option value="INKJET">Inkjet</option>
                    <option value="DOT_MATRIX">Dot Matrix</option>
                    <option value="THERMAL">Thermal</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={printerFormData.isColor}
                      onChange={(e) =>
                        setPrinterFormData({ 
                          ...printerFormData, 
                          isColor: e.target.checked,
                          colors: e.target.checked ? [] : [] // Reset colors when toggling
                        })
                      }
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">Color Printer</span>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Check this if the printer supports color printing
                  </p>
                </div>

                {/* Color Selection - only shown when isColor is checked */}
                {printerFormData.isColor && (
                  <div className="border border-input rounded-md p-4 bg-muted/30">
                    <Label className="mb-2 block">Select Supported Colors *</Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Choose which toner colors this printer uses
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {AVAILABLE_COLORS.map((colorOption) => (
                        <label
                          key={colorOption.value}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer border transition-all',
                            printerFormData.colors.includes(colorOption.value)
                              ? 'bg-primary/10 border-primary'
                              : 'bg-background border-input hover:bg-muted/50'
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={printerFormData.colors.includes(colorOption.value)}
                            onChange={() => toggleColorSelection(colorOption.value)}
                            className="sr-only"
                          />
                          <span className={cn('w-4 h-4 rounded-full', colorOption.color)} />
                          <span className="text-sm font-medium">{colorOption.label}</span>
                          {printerFormData.colors.includes(colorOption.value) && (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          )}
                        </label>
                      ))}
                    </div>
                    {printerFormData.colors.length === 0 && (
                      <p className="text-xs text-destructive mt-2">Please select at least one color</p>
                    )}
                  </div>
                )}

                {/* Compatible Toner Categories */}
                <div>
                  <Label>Compatible Toner Categories</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which toner categories are compatible with this printer
                  </p>
                  <div className="mt-2 max-h-48 overflow-y-auto border border-input rounded-md p-2">
                    {allCategories.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">No toner categories available</p>
                    ) : (
                      <div className="space-y-2">
                        {allCategories.map((category) => (
                          <label
                            key={category.id}
                            className={cn(
                              'flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50',
                              printerFormData.compatibleTonerIds.includes(category.id) && 'bg-primary/10 border border-primary/30'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={printerFormData.compatibleTonerIds.includes(category.id)}
                              onChange={() => toggleTonerSelection(category.id)}
                              className="w-4 h-4"
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{category.name}</span>
                              {category.isColor && (
                                <span className="flex gap-0.5">
                                  <span className="w-3 h-3 rounded-full bg-gray-900" />
                                  <span className="w-3 h-3 rounded-full bg-cyan-500" />
                                  <span className="w-3 h-3 rounded-full bg-pink-500" />
                                  <span className="w-3 h-3 rounded-full bg-yellow-400" />
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={closePrinterModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createPrinterMutation.isPending || updatePrinterMutation.isPending}
                  >
                    {editingPrinter ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
